/**
 * Download an application's tests as a zip: the whole application, or one
 * variant.
 *
 * Split out of `DownloadRepoButton` (#2194) because the action moved into
 * a menu, and a menu item cannot carry the button's inline error span —
 * the menu has closed by the time the request fails. The guard, the
 * loading flag and the request are the parts worth keeping; where the
 * failure is shown is the caller's business.
 *
 * `DownloadRepoButton` was the only other caller and went with the change,
 * so this is the one download path rather than one per shape.
 *
 * With a variant it asks the variant export, which returns only that
 * variant's current files and no history. Without one it asks the
 * application export, which returns every variant with full history.
 */
import { useState } from 'react'
import { DownloadError, downloadFromUrl } from '@/lib/downloadFromUrl'
import { workspaceApiUrl } from '@/lib/api'

/** How a download ended. A failure carries the HTTP status when the server
 * answered, so a caller can show the server's reason for an expected
 * refusal (409: nothing to download yet) and its own message otherwise. */
export type RepoDownloadResult =
  | { ok: true }
  | { ok: false; status?: number; message: string }

export interface RepoDownload {
  /** Start the download. Never throws; a caller that shows its own message
   * reads the result. */
  download: () => Promise<RepoDownloadResult>
  loading: boolean
  /** The last failure, or null. A caller rendering inline reads this; one
   * using a toast can ignore it and read `download`'s result. */
  error: string | null
}

export function useRepoDownload(applicationId: string, variantId?: string): RepoDownload {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function download(): Promise<RepoDownloadResult> {
    // Guard re-entrancy: the trigger is disabled while loading, but a very
    // fast double-click can fire before that re-renders — without this it
    // is two downloads (Copilot review, #2055). A menu item cannot be
    // disabled mid-click at all, so the guard matters more here than it
    // did on the button.
    if (loading || !applicationId) return { ok: false, message: 'No application selected' }
    setLoading(true)
    setError(null)
    try {
      // Workspace-scoped: the applications router mounts under
      // /api/workspaces/:workspaceId/…, so the URL needs the active
      // workspace prefix. The filename comes from Content-Disposition.
      const path = variantId
        ? `applications/${applicationId}/variants/${variantId}/tests.zip`
        : `applications/${applicationId}/repo.zip`
      await downloadFromUrl(workspaceApiUrl(path), variantId ? 'tests.zip' : 'repo.zip')
      return { ok: true }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err)
      setError(message)
      return { ok: false, status: err instanceof DownloadError ? err.status : undefined, message }
    } finally {
      setLoading(false)
    }
  }

  return { download, loading, error }
}
